import type { AxiosRequestConfig } from "axios";
import axios from "axios";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import type { AccessCheckResponse, BlobProbeTarget } from "./types/accessCheckApiTypes";

export const getAnonymousAuthBaseUrl = (): string => `${getBackendBaseUrl("anonymous")}/auth`;

// Backend always responds with 204 No Content (empty body) on success — does not return an
// ApiResponse envelope. Synthesize a success response so withErrorHandling treats it as ok.
// Non-2xx (e.g., 429) throws and is surfaced through getApiError as usual.
const requestCode = async (email: string): Promise<ApiResponse<null>> => {
  await axios.post(`${getAnonymousAuthBaseUrl()}/investor-portal/request-code`, { email }, { withCredentials: false });
  return createApiResponse(null);
};

// Backend returns 401 for non-Valid outcomes (InvalidAuthCode | AuthCodeAttemptsExhausted).
// skipAuthRefresh keeps axiosHelper's response interceptor from treating that 401 as an
// expired session and redirecting the user to sign-in. The thrown error carries the
// ApiResponse body, which the caller surfaces via withErrorHandling + getApiError.
const checkAccess = async (email: string, code: string): Promise<ApiResponse<AccessCheckResponse>> => {
  const { data } = await axios.post<ApiResponse<AccessCheckResponse>>(
    `${getAnonymousAuthBaseUrl()}/investor-portal/access-check`,
    { email, code },
    { withCredentials: false, skipAuthRefresh: true } as AxiosRequestConfig
  );
  return data;
};

// Backend returns 401 for non-Valid outcomes; skipAuthRefresh prevents the response interceptor
// from treating that 401 as an expired session. The verification code activated by access-check
// stays valid for ~1 hr, so calling this immediately after a successful access-check works.
const getBlobProbes = async (email: string, code: string): Promise<ApiResponse<BlobProbeTarget[]>> => {
  const { data } = await axios.post<ApiResponse<BlobProbeTarget[]>>(
    `${getAnonymousAuthBaseUrl()}/investor-portal/blob-probes`,
    { email, code },
    { withCredentials: false, skipAuthRefresh: true } as AxiosRequestConfig
  );
  return data;
};

export const accessCheckApi = {
  requestCode,
  checkAccess,
  getBlobProbes,
};
